/**
 * Compare selector (2–3 products)
 * - Adds/removes handles to localStorage
 * - Shows a sticky compare bar
 * - Navigates to compare page with `?compare=handle1,handle2,handle3`
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mod_compare_handles_v1';
  var MAX_ITEMS = 3;
  var MIN_ITEMS = 2;
  var memoryHandles = [];

  // Some browsers/privacy modes can throw on localStorage access (notably Safari private mode).
  // Fall back to sessionStorage or in-memory storage so the UI remains responsive.
  var getStorage = function () {
    var testKey = '__mod_compare_test__';

    try {
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch (e) {}

    try {
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      return window.sessionStorage;
    } catch (e) {}

    return null;
  };

  var storage = getStorage();

  var normalizeHandle = function (handle) {
    return String(handle || '').trim();
  };

  var safeParse = function (value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  };

  var getHandles = function () {
    if (!storage) return memoryHandles.slice(0, MAX_ITEMS);

    try {
      var parsed = safeParse(storage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return memoryHandles.slice(0, MAX_ITEMS);
      memoryHandles = parsed.map(normalizeHandle).filter(Boolean).slice(0, MAX_ITEMS);
      return memoryHandles.slice(0, MAX_ITEMS);
    } catch (e) {
      return memoryHandles.slice(0, MAX_ITEMS);
    }
  };

  var setHandles = function (handles) {
    var next = (handles || []).map(normalizeHandle).filter(Boolean).slice(0, MAX_ITEMS);
    memoryHandles = next;

    if (storage) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {}
    }
    return next;
  };

  var toastTimer = null;
  var showToast = function (message) {
    var toast = document.getElementById('mod-compare-toast');
    if (!toast) return;
    var msg = String(message || '').trim();
    if (!msg) return;

    toast.textContent = msg;
    toast.hidden = false;
    toast.classList.add('is-show');

    if (toastTimer) window.clearTimeout(toastTimer);
    // Keep the toast open long enough to read. Scale time with message length.
    // Rough heuristic: ~3.5s base + ~55ms per character, clamped to 12s.
    var ms = 3500 + msg.length * 55;
    if (ms < 6000) ms = 6000;
    if (ms > 12000) ms = 12000;
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('is-show');
      window.setTimeout(function () {
        toast.hidden = true;
      }, 200);
    }, ms);
  };

  var normalizePath = function (path) {
    var p = String(path || '');
    if (!p.startsWith('/')) p = '/' + p;
    // trim trailing slash except root
    if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
    return p;
  };

  var isDesignMode = function () {
    return !!(window.Shopify && window.Shopify.designMode);
  };

  var isOnComparePage = function () {
    var comparePageUrl = window.MOD_COMPARE_PAGE_URL || '/pages/compare-mattresses';
    var comparePath = normalizePath(comparePageUrl);
    var currentPath = normalizePath(window.location.pathname || '/');
    // Be resilient if compare page handle changes or MOD_COMPARE_PAGE_URL is unset.
    // Also detect compare page by DOM when available.
    if (currentPath === comparePath) return true;
    if (currentPath.startsWith('/pages/compare-mattresses')) return true;
    if (document.querySelector && document.querySelector('.mattress-compare-section')) return true;
    return false;
  };

  var isCompareHref = function (href) {
    var raw = String(href || '').trim();
    if (!raw) return false;
    if (raw.startsWith('#')) return false;
    if (raw.toLowerCase().startsWith('javascript:')) return false;
    if (raw.toLowerCase().startsWith('mailto:')) return false;
    if (raw.toLowerCase().startsWith('tel:')) return false;

    var url;
    try {
      url = new URL(raw, window.location.origin);
    } catch (e) {
      return false;
    }

    // Only intercept same-origin compare links
    if (url.origin !== window.location.origin) return false;

    var comparePageUrl = window.MOD_COMPARE_PAGE_URL || '/pages/compare-mattresses';
    var comparePath = normalizePath(comparePageUrl);
    var targetPath = normalizePath(url.pathname || '/');

    if (targetPath === comparePath) return true;
    if (targetPath.startsWith('/pages/compare-mattresses')) return true;
    return false;
  };

  var toggleHandle = function (handle) {
    var h = normalizeHandle(handle);
    if (!h) return getHandles();

    var handles = getHandles();
    var idx = handles.indexOf(h);

    if (idx >= 0) {
      handles.splice(idx, 1);
      return setHandles(handles);
    }

    if (handles.length >= MAX_ITEMS) {
      // Drop the oldest to keep UX simple.
      handles.shift();
    }
    handles.push(h);
    return setHandles(handles);
  };

  var clearHandles = function () {
    return setHandles([]);
  };

  var buildCompareUrl = function (handles, prefill) {
    var base = window.MOD_COMPARE_PAGE_URL || '/pages/compare-mattresses';
    var url = new URL(base, window.location.origin);
    url.searchParams.set('compare', handles.join(','));
    if (prefill) {
      url.searchParams.set('prefill', '1');
    }
    // Keep it relative (Shopify-friendly)
    return url.toString().replace(window.location.origin, '');
  };

  // Build a list of handles to compare, filling in defaults if user has < MIN_ITEMS selected.
  // Returns { handles: [...], prefilled: boolean }
  var buildPrefillHandles = function (storedHandles) {
    var defaults = window.MOD_COMPARE_DEFAULT_HANDLES || [];
    var result = (storedHandles || []).slice();
    var prefilled = false;

    // Add defaults until we have MAX_ITEMS (or run out of defaults)
    for (var i = 0; i < defaults.length && result.length < MAX_ITEMS; i++) {
      var d = normalizeHandle(defaults[i]);
      if (d && result.indexOf(d) === -1) {
        result.push(d);
        prefilled = true;
      }
    }

    return {
      handles: result.filter(Boolean).slice(0, MAX_ITEMS),
      prefilled: prefilled
    };
  };

  // Navigate to compare page, prefilling with defaults if user has < MIN_ITEMS selected.
  var navigateToCompare = function () {
    var stored = getHandles();
    
    // If user has 2+ selected, navigate normally
    if (stored.length >= MIN_ITEMS) {
      window.location.href = buildCompareUrl(stored, false);
      return;
    }

    // Otherwise, prefill with defaults
    var result = buildPrefillHandles(stored);
    window.location.href = buildCompareUrl(result.handles, result.prefilled);
  };

  var updateButtons = function (handles) {
    var buttons = document.querySelectorAll('[data-compare-toggle][data-compare-handle]');
    buttons.forEach(function (btn) {
      var h = normalizeHandle(btn.dataset.compareHandle);
      var isActive = h && handles.indexOf(h) >= 0;

      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.classList.toggle('is-active', isActive);

      var activeText = btn.dataset.compareActiveText || 'Added';
      var inactiveText = btn.dataset.compareText || 'Compare';
      var nextLabel = isActive ? activeText : inactiveText;

      // If button has a dedicated label node, update it without nuking inner HTML (icons, etc.)
      var labelEl = btn.querySelector('[data-compare-label]');
      if (labelEl) {
        labelEl.textContent = nextLabel;
        return;
      }

      btn.textContent = nextLabel;
    });
  };

  var updateBar = function (handles) {
    var count = handles.length;
    
    // Update all count elements (sidebar, drawer, toggle buttons)
    var countEls = document.querySelectorAll('[data-compare-count]');
    countEls.forEach(function (el) {
      el.textContent = String(count);
    });

    // "Compare now" buttons are always enabled (we prefill with defaults if needed)
    var goBtns = document.querySelectorAll('[data-compare-go]');
    goBtns.forEach(function (btn) {
      btn.disabled = false;
    });

    // Show/hide sidebar (desktop)
    var sidebar = document.getElementById('mod-compare-sidebar');
    if (sidebar) {
      sidebar.hidden = count === 0;
    }

    // Show/hide drawer trigger (mobile)
    var drawer = document.getElementById('mod-compare-drawer');
    if (drawer) {
      drawer.hidden = count === 0;
    }
  };

  var bindToggleButtons = function () {
    // Use event delegation so toggles keep working even if themes/apps re-render the button DOM.
    // (A common failure mode is: old code sets data-compare-bound="1", DOM is replaced, listener is lost,
    // but the attribute remains, making the new button appear "dead".)
    if (document.documentElement.dataset.modCompareToggleDelegated === '1') return;
    document.documentElement.dataset.modCompareToggleDelegated = '1';

    var getToggleEl = function (target) {
      if (!target) return null;
      if (target.matches && target.matches('[data-compare-toggle][data-compare-handle]')) return target;
      if (target.closest) return target.closest('[data-compare-toggle][data-compare-handle]');
      return null;
    };

    var activate = function (btn, e) {
      if (!btn) return;
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

      if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', 'Toggle compare');
      if (!btn.hasAttribute('tabindex') && btn.tabIndex < 0) btn.tabIndex = 0;

      var handle = normalizeHandle(btn.dataset.compareHandle);
      var handles = toggleHandle(handle);
      updateButtons(handles);
      updateBar(handles);

      var isActiveNow = handle && handles.indexOf(handle) >= 0;
      if (isActiveNow) {
        showToast('Added to Compare!');
      } else {
        showToast('Removed from compare.');
      }

      // Auto-expand sidebar/drawer when item is added (if not already visible)
      var sidebar = document.getElementById('mod-compare-sidebar');
      if (sidebar && !sidebar.hidden && sidebar.dataset.expanded !== 'true') {
        sidebar.dataset.expanded = 'true';
        var toggleBtns = sidebar.querySelectorAll('[data-compare-sidebar-toggle]');
        toggleBtns.forEach(function (b) {
          b.setAttribute('aria-expanded', 'true');
        });
      }

      var drawer = document.getElementById('mod-compare-drawer');
      if (drawer && !drawer.hidden && drawer.dataset.open !== 'true') {
        // On mobile, auto-open drawer when item added
        if (window.innerWidth <= 767) {
          drawer.dataset.open = 'true';
        }
      }
    };

    document.addEventListener(
      'click',
      function (e) {
        var btn = getToggleEl(e && e.target);
        if (!btn) return;
        activate(btn, e);
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (e) {
        if (!e) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var btn = getToggleEl(e.target);
        if (!btn) return;
        activate(btn, e);
      },
      true
    );
  };

  var bindNavLinks = function () {
    var navLinks = document.querySelectorAll('[data-compare-nav]');
    navLinks.forEach(function (link) {
      if (link.dataset.compareBound === '1') return;
      link.dataset.compareBound = '1';

      var handleClick = function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        navigateToCompare();
      };

      link.addEventListener('click', handleClick);
    });
  };

  var bindGlobalCompareLinks = function () {
    // Catch any compare-page link site-wide (even plain <a href="/pages/compare-mattresses">)
    if (document.documentElement.dataset.modCompareCompareLinkDelegated === '1') return;
    document.documentElement.dataset.modCompareCompareLinkDelegated = '1';

    var getLinkEl = function (target) {
      if (!target) return null;
      if (target.matches && target.matches('a[href]')) return target;
      if (target.closest) return target.closest('a[href]');
      return null;
    };

    var activate = function (link, e) {
      if (!link) return;
      if (!isCompareHref(link.getAttribute('href'))) return;

      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

      navigateToCompare();
    };

    document.addEventListener(
      'click',
      function (e) {
        var link = getLinkEl(e && e.target);
        if (!link) return;
        activate(link, e);
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (e) {
        if (!e) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var link = getLinkEl(e.target);
        if (!link) return;
        activate(link, e);
      },
      true
    );
  };

  var bindSidebar = function () {
    var sidebar = document.getElementById('mod-compare-sidebar');
    if (!sidebar) return;

    // Toggle buttons
    var toggleBtns = sidebar.querySelectorAll('[data-compare-sidebar-toggle]');
    toggleBtns.forEach(function (btn) {
      if (btn.dataset.compareBound === '1') return;
      btn.dataset.compareBound = '1';
      btn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        var isExpanded = sidebar.dataset.expanded === 'true';
        sidebar.dataset.expanded = isExpanded ? 'false' : 'true';
        toggleBtns.forEach(function (b) {
          b.setAttribute('aria-expanded', !isExpanded ? 'true' : 'false');
        });
      });
    });

    // Compare now and Clear buttons
    var goBtn = sidebar.querySelector('[data-compare-go]');
    var clearBtn = sidebar.querySelector('[data-compare-clear]');

    if (goBtn && !goBtn.dataset.compareBound) {
      goBtn.dataset.compareBound = '1';
      goBtn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        navigateToCompare();
      });
    }

    if (clearBtn && !clearBtn.dataset.compareBound) {
      clearBtn.dataset.compareBound = '1';
      clearBtn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        var handles = clearHandles();
        updateButtons(handles);
        updateBar(handles);
      });
    }
  };

  var bindDrawer = function () {
    var drawer = document.getElementById('mod-compare-drawer');
    if (!drawer) return;

    // Open trigger
    var openBtn = drawer.querySelector('[data-compare-drawer-open]');
    if (openBtn && !openBtn.dataset.compareBound) {
      openBtn.dataset.compareBound = '1';
      openBtn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        drawer.dataset.open = 'true';
      });
    }

    // Close buttons (close btn and overlay)
    var closeBtns = drawer.querySelectorAll('[data-compare-drawer-close]');
    closeBtns.forEach(function (btn) {
      if (btn.dataset.compareBound === '1') return;
      btn.dataset.compareBound = '1';
      btn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        drawer.dataset.open = 'false';
      });
    });

    // Compare now and Clear buttons
    var goBtn = drawer.querySelector('[data-compare-go]');
    var clearBtn = drawer.querySelector('[data-compare-clear]');

    if (goBtn && !goBtn.dataset.compareBound) {
      goBtn.dataset.compareBound = '1';
      goBtn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        navigateToCompare();
      });
    }

    if (clearBtn && !clearBtn.dataset.compareBound) {
      clearBtn.dataset.compareBound = '1';
      clearBtn.addEventListener('click', function (e) {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        var handles = clearHandles();
        updateButtons(handles);
        updateBar(handles);
      });
    }
  };

  var bindBar = function () {
    bindSidebar();
    bindDrawer();
  };

  var syncFromUrlIfPresent = function () {
    // In the theme editor, avoid rewriting the preview URL / selection state.
    // (This is a common cause of "it keeps showing the old mattresses" while customizing.)
    if (isDesignMode()) return;

    var params = new URLSearchParams(window.location.search || '');
    if (params.has('compare')) {
      var raw = params.get('compare') || '';
      var handles = raw.split(',').map(normalizeHandle).filter(Boolean).slice(0, MAX_ITEMS);
      setHandles(handles);
      
      // If on compare page with insufficient handles and NOT already prefilled, redirect with prefill
      if (isOnComparePage() && handles.length < MIN_ITEMS && !params.has('prefill')) {
        var result = buildPrefillHandles(handles);
        window.location.href = buildCompareUrl(result.handles, result.prefilled);
      }
      return;
    }

    // If user lands on compare page without ?compare:
    // - if they have 2+ in storage: hydrate URL
    // - else: redirect with prefilled defaults
    if (!isOnComparePage()) return;

    var stored = getHandles();
    if (stored.length >= MIN_ITEMS) {
      window.location.href = buildCompareUrl(stored, false);
      return;
    }

    // Prefill with defaults
    var result = buildPrefillHandles(stored);
    window.location.href = buildCompareUrl(result.handles, result.prefilled);
  };

  var init = function () {
    syncFromUrlIfPresent();

    var handles = getHandles();
    updateButtons(handles);
    updateBar(handles);

    bindToggleButtons();
    bindNavLinks();
    bindGlobalCompareLinks();
    bindBar();

    // If content is injected (infinite scroll, filters), re-bind.
    var observer = new MutationObserver(function (mutations) {
      if (!mutations || !mutations.length) return;
      bindNavLinks();
      bindBar();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

